import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client.js';
import { openTenantReportPrintWindow } from '../../utils/adminTenantReportPrint.js';
import { orderStatusLabelPtBr } from '../../utils/orderStatus.js';

function formatBrl(n) {
  if (n == null || n === '' || Number.isNaN(Number(n))) return '—';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(n));
}

/** @param {{ tenant: { id: number, nome?: string, slug?: string }, onClose: () => void, reportPathPrefix?: string, variant?: 'admin' | 'tenant' }} props */
export function AdminTenantReportModal({
  tenant,
  onClose,
  reportPathPrefix = 'admin/tenants',
  variant = 'admin',
}) {
  const [days, setDays] = useState(30);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    if (!tenant?.id) return;
    setLoading(true);
    setErr('');
    try {
      const prefix = String(reportPathPrefix || 'admin/tenants').replace(/^\/+|\/+$/g, '');
      const d = await api(`/${prefix}/${tenant.id}/report?days=${encodeURIComponent(String(days))}`);
      setReport(d);
    } catch (e) {
      setReport(null);
      setErr(e.message || 'Falha ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, [tenant?.id, days, reportPathPrefix]);

  useEffect(() => {
    load();
  }, [load]);

  if (!tenant) return null;

  const prod = report?.products && typeof report.products === 'object' ? report.products : {};
  const ordersAll = Array.isArray(report?.orders_by_status_all) ? report.orders_by_status_all : [];
  const ordersPeriod = Array.isArray(report?.orders_by_status_period) ? report.orders_by_status_period : [];
  const recent = Array.isArray(report?.recent_orders) ? report.recent_orders : [];
  const sub = report?.saas_subscription && typeof report.saas_subscription === 'object' ? report.saas_subscription : {};

  function periodTitle() {
    if (days === 0) return 'Todo o período';
    return `Últimos ${days} dias`;
  }

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm"
      role="presentation"
      onClick={() => !loading && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-tenant-report-title"
        className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl border border-ink/10 bg-white shadow-2xl dark:border-white/10 dark:bg-[#161f1c]"
        onClick={(ev) => ev.stopPropagation()}
      >
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-ink/10 px-5 py-4 dark:border-white/10">
          <div>
            <h3 id="admin-tenant-report-title" className="font-display text-lg font-semibold text-ink dark:text-mist">
              Relatório · {tenant.nome || `Empresa #${tenant.id}`}
            </h3>
            <p className="mt-0.5 font-mono text-xs text-ink/55 dark:text-frost-muted">
              /{tenant.slug || '—'} · #{tenant.id}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-ink/15 px-3 py-1 text-xs font-semibold dark:border-white/20"
          >
            Fechar
          </button>
        </div>

        <div className="flex flex-wrap items-end gap-3 border-b border-ink/10 px-5 py-3 dark:border-white/10">
          <label className="flex flex-col text-[11px] font-semibold text-ink/80 dark:text-frost">
            Janela dos pedidos (tabela &quot;no período&quot;)
            <select
              className="mt-1 rounded-xl border border-ink/10 bg-white px-2 py-1.5 text-xs dark:border-white/10 dark:bg-[#252f2a]"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              <option value={7}>7 dias</option>
              <option value={30}>30 dias</option>
              <option value={90}>90 dias</option>
              <option value={365}>365 dias</option>
              <option value={0}>Todo o período</option>
            </select>
          </label>
          <button
            type="button"
            disabled={loading}
            onClick={() => load()}
            className="rounded-2xl border border-ink/15 px-4 py-2 text-xs font-semibold dark:border-white/20"
          >
            {loading ? 'A carregar…' : 'Atualizar'}
          </button>
          <button
            type="button"
            disabled={loading || !report}
            onClick={() => report && openTenantReportPrintWindow(report, { variant })}
            className="rounded-2xl bg-emerald-700 px-4 py-2 text-xs font-semibold text-white disabled:opacity-40 dark:bg-emerald-600"
            title="Abre uma página para imprimir — no diálogo escolha Guardar como PDF"
          >
            PDF / Imprimir
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 text-sm">
          {err ? (
            <div className="rounded-xl border border-accent2/40 bg-accent2/10 px-3 py-2 text-sm font-semibold text-accent2">
              {err}
            </div>
          ) : null}

          {!loading && report ? (
            <div className="space-y-6">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <div className="rounded-2xl border border-ink/10 bg-black/[0.03] p-3 dark:border-white/10 dark:bg-white/[0.04]">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-ink/50 dark:text-frost-muted">
                    Produtos
                  </div>
                  <div className="mt-1 font-mono text-lg font-semibold">{prod.total ?? 0}</div>
                  <div className="text-xs text-ink/65 dark:text-frost-muted">
                    Visíveis: {prod.active_visible ?? 0} · Stock controlado: {prod.stock_tracked ?? 0}
                  </div>
                </div>
                <div className="rounded-2xl border border-ink/10 bg-black/[0.03] p-3 dark:border-white/10 dark:bg-white/[0.04]">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-ink/50 dark:text-frost-muted">
                    Clientes
                  </div>
                  <div className="mt-1 font-mono text-lg font-semibold">{report.customers ?? 0}</div>
                  <div className="text-xs text-ink/65 dark:text-frost-muted">Cadastro na loja</div>
                </div>
                <div className="rounded-2xl border border-ink/10 bg-black/[0.03] p-3 dark:border-white/10 dark:bg-white/[0.04]">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-ink/50 dark:text-frost-muted">
                    SaaS em aberto
                  </div>
                  <div className="mt-1 font-mono text-lg font-semibold">{sub.pending_charges ?? 0}</div>
                  <div className="text-xs text-ink/65 dark:text-frost-muted">{formatBrl(sub.pending_amount_brl)}</div>
                </div>
              </div>

              <div>
                <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-ink/55 dark:text-frost-muted">
                  Pedidos por estado (sempre · todo o histórico)
                </h4>
                <div className="overflow-x-auto rounded-xl border border-ink/10 dark:border-white/10">
                  <table className="w-full min-w-[280px] text-left text-xs">
                    <thead className="bg-ink/[0.06] dark:bg-white/[0.06]">
                      <tr>
                        <th className="px-3 py-2 font-semibold">Estado</th>
                        <th className="px-3 py-2 font-semibold">Qtd</th>
                        <th className="px-3 py-2 font-semibold">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ordersAll.map((r) => (
                        <tr key={String(r.status)} className="border-t border-ink/10 dark:border-white/10">
                          <td className="px-3 py-2">{orderStatusLabelPtBr(r.status)}</td>
                          <td className="px-3 py-2">{r.cnt}</td>
                          <td className="px-3 py-2">{formatBrl(r.revenue)}</td>
                        </tr>
                      ))}
                      {!ordersAll.length ? (
                        <tr>
                          <td colSpan={3} className="px-3 py-4 text-center text-ink/50 dark:text-frost-muted">
                            Nenhum pedido.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-ink/55 dark:text-frost-muted">
                  Pedidos por estado ({periodTitle()})
                </h4>
                <div className="overflow-x-auto rounded-xl border border-ink/10 dark:border-white/10">
                  <table className="w-full min-w-[280px] text-left text-xs">
                    <thead className="bg-emerald-900/15 dark:bg-emerald-500/10">
                      <tr>
                        <th className="px-3 py-2 font-semibold">Estado</th>
                        <th className="px-3 py-2 font-semibold">Qtd</th>
                        <th className="px-3 py-2 font-semibold">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ordersPeriod.map((r) => (
                        <tr key={String(r.status)} className="border-t border-ink/10 dark:border-white/10">
                          <td className="px-3 py-2">{orderStatusLabelPtBr(r.status)}</td>
                          <td className="px-3 py-2">{r.cnt}</td>
                          <td className="px-3 py-2">{formatBrl(r.revenue)}</td>
                        </tr>
                      ))}
                      {!ordersPeriod.length ? (
                        <tr>
                          <td colSpan={3} className="px-3 py-4 text-center text-ink/50 dark:text-frost-muted">
                            Sem pedidos nesta janela.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-ink/55 dark:text-frost-muted">
                  Pedidos recentes (amostra)
                </h4>
                <div className="max-h-56 overflow-y-auto rounded-xl border border-ink/10 dark:border-white/10">
                  <table className="w-full min-w-[420px] text-left text-[11px]">
                    <thead className="sticky top-0 bg-ink/[0.06] dark:bg-white/[0.06]">
                      <tr>
                        <th className="px-2 py-1.5">ID</th>
                        <th className="px-2 py-1.5">Data</th>
                        <th className="px-2 py-1.5">Cliente</th>
                        <th className="px-2 py-1.5">Estado</th>
                        <th className="px-2 py-1.5">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recent.slice(0, 15).map((o) => (
                        <tr key={String(o.id)} className="border-t border-ink/10 dark:border-white/10">
                          <td className="px-2 py-1 font-mono">{o.id}</td>
                          <td className="px-2 py-1 whitespace-nowrap">{String(o.created_at || '').slice(0, 16)}</td>
                          <td className="px-2 py-1 max-w-[140px] truncate" title={String(o.customer_nome || '')}>
                            {o.customer_nome || '—'}
                          </td>
                          <td className="px-2 py-1">{orderStatusLabelPtBr(o.status)}</td>
                          <td className="px-2 py-1">{formatBrl(o.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : loading ? (
            <p className="py-8 text-center text-ink/50 dark:text-frost-muted">A carregar dados…</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
